import { useEffect, useState } from "react";

import { ConsultationNotesTable } from "features/consultations/components/ConsultationEdit/components/ConsultationNotesTable/ConsultationNotesTable";
import { ConsultationEdit } from "features/consultations/components/ConsultationEdit/ConsultationEdit.jsx";
import { useActiveConsultationNotes } from "features/consultations/hooks/useActiveConsultationNotes";
import { useDeleteConsultationNote } from "features/consultations/hooks/useDeleteConsultationNote";

import s from "./tabs.module.css";

export const NoteTab = ({
  consultation,
  staffName,
  isDisabled,
  isReadOnly = false,
  currentStaffMemberId,
}) => {
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isNewNote, setIsNewNote] = useState(false);

  const { data: consultationNotes } = useActiveConsultationNotes(consultation?.id);
  const { mutate: deleteNote } = useDeleteConsultationNote();

  const handleDeleteNote = (note) => {
    const isActive = note.id === activeNoteId;
    let nextNote = null;

    if (isActive && consultationNotes) {
      const currentIndex = consultationNotes.findIndex((n) => n.id === note.id);
      nextNote = consultationNotes[currentIndex + 1] || consultationNotes[currentIndex - 1] || null;
    }

    deleteNote(note, {
      onSuccess: () => {
        if (isActive) {
          setSelectedNote(nextNote);
          setActiveNoteId(nextNote?.id ?? null);
          setIsNewNote(!nextNote);
        }
      },
    });
  };

  useEffect(() => {
    // Reset when switching consultations — must be declared before auto-selection
    // so auto-selection fires after the reset in the same render cycle.
    setActiveNoteId(null);
    setSelectedNote(null);
    setIsNewNote(false);
  }, [consultation?.id]);

  useEffect(() => {
    if (consultationNotes?.length > 0 && !activeNoteId && !isNewNote) {
      const first = consultationNotes[0];
      setSelectedNote(first);
      setActiveNoteId(first.id);
    }
  }, [consultationNotes, activeNoteId, isNewNote]);

  const getNoteCreatorId = (note) =>
    note?.createdByStaffMember?.id ?? note?.staffMemberId;

  const isSelectedNoteFromOtherDoctor =
    !!selectedNote &&
    !!currentStaffMemberId &&
    String(getNoteCreatorId(selectedNote)) !== String(currentStaffMemberId);

  const isNoteReadOnly = isReadOnly || isSelectedNoteFromOtherDoctor;

  return (
    <>
      {!!isSelectedNoteFromOtherDoctor && !!currentStaffMemberId && !isReadOnly && (
        <div className={s.otherDoctorBanner}>
          This note was added by another doctor and cannot be edited.
        </div>
      )}
      <ConsultationEdit
        consultation={consultation}
        isDisabled={isDisabled}
        isReadOnly={isNoteReadOnly}
        autoSave={!isNoteReadOnly}
        canCreateNote={!isReadOnly}
        staffName={staffName}
        selectedNote={selectedNote}
        onNoteChange={(id) => {
          setActiveNoteId(id ?? null);
          if (id == null) {
            setSelectedNote(null);
            setIsNewNote(true);
          } else {
            setIsNewNote(false);
          }
        }}
        onNewNoteSaved={(savedNote) => {
          setActiveNoteId(savedNote.id);
          setIsNewNote(false);
        }}
      />
      <div className={s.sectionWrapper}>
        <div className={s.sectionHeader}>
          <h3 className={s.sectionTitle}>Notes for this Consultation</h3>
        </div>
        <div className={s.sectionContent}>
          <ConsultationNotesTable
            notes={consultationNotes || []}
            currentNoteId={activeNoteId}
            currentStaffMemberId={currentStaffMemberId}
            onEdit={(note) => {
              setSelectedNote(note);
              setActiveNoteId(note.id);
              setIsNewNote(false);
            }}
            onDelete={isReadOnly ? undefined : handleDeleteNote}
          />
        </div>
      </div>
    </>
  );
};
